import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BottomTabNav from '../shared/components/BottomTabNav';
import Footer from '../shared/components/Footer';
import './CustomerSupport.css';

const CustomerSupport = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const supportCategories = [
    {
      id: 'orders',
      title: 'Order Issues',
      icon: '📦',
      description: 'Problems with your orders, delivery, or refunds',
      color: '#10b981'
    },
    {
      id: 'account',
      title: 'Account & Profile',
      icon: '👤',
      description: 'Login issues, profile updates, or account settings',
      color: '#3b82f6'
    },
    {
      id: 'payment',
      title: 'Payment & Billing',
      icon: '💳',
      description: 'Payment failures, billing questions, or refunds',
      color: '#f59e0b'
    },
    {
      id: 'technical',
      title: 'Technical Support',
      icon: '⚙️',
      description: 'App bugs, performance issues, or technical problems',
      color: '#8b5cf6'
    },
    {
      id: 'feedback',
      title: 'Feedback & Suggestions',
      icon: '💬',
      description: 'Share your thoughts or suggest improvements',
      color: '#06b6d4'
    },
    {
      id: 'other',
      title: 'Other Issues',
      icon: '❓',
      description: 'Any other questions or concerns',
      color: '#64748b'
    }
  ];

  const faqData = {
    orders: [
      {
        question: 'How can I track my order?',
        answer: 'You can track your order in real-time by going to "My Orders" section in your profile. You\'ll see the current status and estimated delivery time.'
      },
      {
        question: 'Can I cancel my order?',
        answer: 'Yes, you can cancel your order within 5 minutes of placing it. After that, please contact our support team for assistance.'
      },
      {
        question: 'What if my order is delayed?',
        answer: 'If your order is significantly delayed, you\'ll receive automatic notifications. You can also contact our support team for immediate assistance.'
      },
      {
        question: 'How do I report a problem with my order?',
        answer: 'Go to your order history, select the problematic order, and click "Report Issue". You can also contact us directly through this support page.'
      }
    ],
    account: [
      {
        question: 'I forgot my password. How can I reset it?',
        answer: 'Click on "Forgot Password" on the login page and enter your email address. You\'ll receive a password reset link within a few minutes.'
      },
      {
        question: 'How do I update my profile information?',
        answer: 'Go to your Profile page and click "Edit Profile" or "Personal Information" to update your details.'
      },
      {
        question: 'Can I delete my account?',
        answer: 'Yes, you can request account deletion by contacting our support team. Please note that this action cannot be undone.'
      },
      {
        question: 'How do I change my delivery address?',
        answer: 'You can update your address in the "Personal Information" section of your profile, or add a new address during checkout.'
      }
    ],
    payment: [
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major credit cards, debit cards, digital wallets (Apple Pay, Google Pay), and cash on delivery.'
      },
      {
        question: 'Why was my payment declined?',
        answer: 'Payment can be declined due to insufficient funds, expired card, or security restrictions. Please check with your bank or try a different payment method.'
      },
      {
        question: 'How long do refunds take?',
        answer: 'Refunds are typically processed within 3-5 business days. The exact time depends on your bank or payment provider.'
      },
      {
        question: 'Can I get a receipt for my order?',
        answer: 'Yes, you\'ll receive an email receipt after each order. You can also view and download receipts from your order history.'
      }
    ],
    technical: [
      {
        question: 'The app is running slowly. What should I do?',
        answer: 'Try closing and reopening the app, check your internet connection, and ensure you have the latest app version installed.'
      },
      {
        question: 'I\'m having trouble logging in',
        answer: 'Make sure you\'re using the correct email and password. Clear your browser cache or try logging in from a different device.'
      },
      {
        question: 'The app keeps crashing',
        answer: 'Update to the latest version of the app. If the problem persists, restart your device and contact our technical support team.'
      },
      {
        question: 'I can\'t see my order history',
        answer: 'Make sure you\'re logged into the correct account. If the issue persists, contact our support team for assistance.'
      }
    ]
  };

  const contactMethods = [
    {
      title: 'Phone Support',
      description: '24/7 immediate assistance',
      contact: '+1 (555) 123-4567',
      icon: '📞',
      action: 'Call Now',
      color: '#10b981'
    },
    {
      title: 'Email Support',
      description: 'Detailed help within 2 hours',
      contact: 'support@zepeat.com',
      icon: '📧',
      action: 'Send Email',
      color: '#3b82f6'
    }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="App">
      <div className="support-container">
        <div className="support-content">
          {/* Header */}
          <div className="support-header">
            <Link to="/profile" className="back-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <h1 className="support-title">Help & Support</h1>
            <div className="header-spacer"></div>
          </div>

          {/* Quick Contact */}
          <div className="quick-contact">
            <h2 className="quick-contact-title">Need immediate help?</h2>
            <div className="contact-methods">
              {contactMethods.map((method, index) => (
                <div key={index} className="contact-method-card">
                  <div className="contact-method-icon" style={{ backgroundColor: method.color }}>
                    {method.icon}
                  </div>
                  <div className="contact-method-content">
                    <h3>{method.title}</h3>
                    <p>{method.description}</p>
                    <span className="contact-info">{method.contact}</span>
                  </div>
                  <button className="contact-action-btn" style={{ borderColor: method.color, color: method.color }}>
                    {method.action}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Support Categories */}
          <div className="support-categories">
            <h2 className="section-title">What can we help you with?</h2>
            <div className="categories-grid">
              {supportCategories.map((category) => (
                <div
                  key={category.id}
                  className={`category-card ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
                  style={{ borderColor: selectedCategory === category.id ? category.color : '#e5e7eb' }}
                >
                  <div className="category-icon" style={{ backgroundColor: category.color }}>
                    {category.icon}
                  </div>
                  <div className="category-content">
                    <h3>{category.title}</h3>
                    <p>{category.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          {selectedCategory && faqData[selectedCategory] && (
            <div className="faq-section">
              <h2 className="section-title">Frequently Asked Questions</h2>
              <div className="faq-list">
                {faqData[selectedCategory].map((faq, index) => (
                  <div key={index} className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}>
                    <div className="faq-question" onClick={() => toggleFaq(index)}>
                      <h3>{faq.question}</h3>
                      <div className={`faq-toggle ${expandedFaq === index ? 'expanded' : ''}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    {expandedFaq === index && (
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Resources */}
          <div className="additional-resources">
            <h2 className="section-title">Additional Resources</h2>
            <div className="resources-grid">
              <Link to="/about" className="resource-card">
                <div className="resource-icon">ℹ️</div>
                <div className="resource-content">
                  <h3>About ZepEat</h3>
                  <p>Learn more about our services and company</p>
                </div>
              </Link>
              
              <Link to="/terms" className="resource-card">
                <div className="resource-icon">📋</div>
                <div className="resource-content">
                  <h3>Terms of Service</h3>
                  <p>Read our terms and conditions</p>
                </div>
              </Link>
              
              <Link to="/privacy" className="resource-card">
                <div className="resource-icon">🔒</div>
                <div className="resource-content">
                  <h3>Privacy Policy</h3>
                  <p>Understand how we protect your data</p>
                </div>
              </Link>
              
              <Link to="/contact" className="resource-card">
                <div className="resource-icon">📞</div>
                <div className="resource-content">
                  <h3>Contact Us</h3>
                  <p>Get in touch with our team</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <Footer websiteType="customer" />
      <BottomTabNav />
    </div>
  );
};

export default CustomerSupport;
